/**
 * HurricaneBanner — yellow alert band shown on every authenticated route
 * when hurricane season is active (force-flagged for demo).
 * Mounted in (app)/layout.tsx so it appears on all authenticated surfaces.
 * Atlas: E5.
 */
"use client";

import Link from "next/link";
import { AlertTriangle } from "lucide-react";
import { useHurricaneAnchor, useHurricaneSeason, formatStormEta } from "@petci/shared-ui";
import { getDemoStorm } from "@petci/fixtures";
import { useT, useLocaleTag } from "@/lib/locale";

export function HurricaneBanner() {
  const t = useT();
  const tag = useLocaleTag();
  const inSeason = useHurricaneSeason(process.env.NEXT_PUBLIC_FORCE_SEASON);
  const now = useHurricaneAnchor(process.env.NEXT_PUBLIC_HURRICANE_ANCHOR_NOW);
  const storm = getDemoStorm(now);
  if (!inSeason) return null;

  return (
    <div
      role="alert"
      className="bg-semantic-warning-strong text-white text-sm font-medium px-4 py-2 flex items-center justify-between gap-3"
    >
      <span className="flex items-center gap-2">
        <AlertTriangle size={16} aria-hidden className="flex-shrink-0" />
        {/* Storm name stays verbatim (proper noun); the ETA localizes via D12.
            The fixture `storm.message` remains the mobile English SSoT. */}
        {t("hurricane.stormBannerMessage", {
          name: storm.name,
          eta: formatStormEta(storm.etaIso, tag),
        })}
      </span>
      {/* D-7: web chrome carries a ≥24px effective-target floor (WCAG 2.5.8 AA);
          the 44px bar governs the native APK only. This link's box was the text
          line alone (~20px). `py-1 -my-1` grows the CLICKABLE box to 28px while
          the negative margin keeps its outer size at 20px, so the banner's own
          height is unchanged — hit area without a layout shift on a band that
          renders above every authenticated route. */}
      <Link
        href="/hurricane"
        className="flex-shrink-0 inline-flex items-center py-1 -my-1 underline hover:no-underline font-semibold text-white transition-colors duration-fast"
      >
        {t("hurricane.prepModeLink")}
      </Link>
    </div>
  );
}
